import React, { useCallback, useMemo, useState, useContext, ChangeEvent } from 'react';
import { IStyle } from 'Styles';
import './style.scss';
import { handleTableFilter } from './baseGroup';
import { GroupDTO, IUserDTO, UserDTO } from 'business/models/Models';
import RMModal from 'components/modal/RMModal';
import UserListContainer from './user';
import { FormActionButton } from 'components/Button';
import { UserGroupContext } from '../../Licensing/userSetup';
import { cloneDeep } from 'lodash';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { showAlertSnackBar } from 'utils/Global';
import HeaderBar from 'components/main/Setup/CrystalContainer/HeaderBar';
import RMTable from 'components/customComponents/Controls/RMTable';
import { ALL_ROCKMAKER_USERS } from '../constants';

interface Props {
  selectedGroup: GroupDTO;
}

const getTableColumns: object[] = [
  {
    accessor: 'userName',
    Filter: handleTableFilter('User Name'),
  },
  {
    accessor: 'groupAdmin',
    Header: 'Group Admin',
    className: '!justify-center',
  },
];

const groupsNotAllowedToBeRemoved = [ALL_ROCKMAKER_USERS];

interface UserListProps extends Props {
  toggleModalShow: (value: boolean) => void;
  updateGroupMembers: (users) => void;
}

const selectedColumnsToShowInUserList = ['name'];
const UserList = (props: UserListProps) => {
  const { selectedGroup, toggleModalShow, updateGroupMembers } = props;
  const [selectedUsers, setSelectedUsers] = useState<UserDTO[]>([]);
  const { tempState } = useContext(UserGroupContext);
  const [editIdUser, setEditIdUser] = useState<number>(0);

  const onSaveClick = useCallback(() => {
    updateGroupMembers([...selectedGroup.users, ...selectedUsers]);
    toggleModalShow(false);
  }, [updateGroupMembers, selectedGroup, selectedUsers]);
  const onDiscardClick = useCallback(() => toggleModalShow(false), []);
  const handleSetSelectedUser = useCallback(
    (userIndices: number[]) => {
      const existingUserIds = selectedGroup.users.map((u) => u.id);
      const filteredUserList = tempState.users.filter(
        (u) => !existingUserIds.includes(u.id),
      );
      setSelectedUsers(filteredUserList.filter((_v, _ix) => userIndices.includes(_ix)));
    },
    [setSelectedUsers, tempState, selectedGroup],
  );

  return (
    <div id="membergroup-userlist-wrapper" style={styles.userListRoot}>
      <UserListContainer
        onSelectedUserChange={handleSetSelectedUser}
        selectedUsers={selectedUsers}
        hideActionContainer={true}
        columnsToShow={selectedColumnsToShowInUserList}
        headerText={''}
        tableContainerStyle={styles.userTableContainerStyle}
        setSelectedUsers={setSelectedUsers}
        showUnsavedUsers={false}
        userIdsToHide={selectedGroup.users.map((u) => u.id)}
        editId={editIdUser}
        setEditId={setEditIdUser}
      />
      <div id="membergroup-userlist-actionbutton" style={styles.userListButtons}>
        <FormActionButton
          onDiscardClick={onDiscardClick}
          onSaveClick={onSaveClick}
          saveText={'Add'}
          discardText={'Cancel'}
          saveDisabled={!selectedUsers.length}
        />
      </div>
    </div>
  );
};

const MemberGroup = (props: Props) => {
  const { selectedGroup } = props;

  const { tempState, setTempState } = useContext(UserGroupContext);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [showModal, setShowModal] = useState(false);
  const selectedUserIds = useMemo(
    () => selectedMembers.map((m) => m.id),
    [selectedMembers],
  );

  if (selectedRows.length && selectedMembers.length && !selectedGroup) {
    setSelectedMembers([]);
    setSelectedRows([]);
  }

  const updateSelectedGroupMembers = useCallback(
    (users: IUserDTO[], updateUsersList = true) => {
      const _userIds = users.map((u) => u.id);
      setTempState((prv) => {
        let selectedGroupIndex = null;
        const filteredGroups = prv.groups.filter((u, _ix) => {
          if (u.id !== selectedGroup.id) {
            return true;
          }

          selectedGroupIndex = _ix;
          return false;
        });

        filteredGroups.splice(selectedGroupIndex, 0, {
          ...cloneDeep(selectedGroup),
          users,
        });

        // Add group to each selected user.

        const filteredUsers = cloneDeep(prv.users).map((_user: UserDTO, _ix) => {
          if (!_userIds.includes(_user.id)) {
            return _user;
          }

          _user.groups.push({
            ...cloneDeep(selectedGroup),
            isPrimaryGroup: false,
          } as GroupDTO);

          return _user;
        });

        return {
          ...prv,
          users: updateUsersList ? filteredUsers : prv.users,
          groups: filteredGroups,
        };
      });
    },
    [selectedGroup, selectedRows],
  );

  const handleGroupAdminSelection = useCallback(
    (event: ChangeEvent<HTMLInputElement>, user: IUserDTO) => {
      let selectedUserIndex = null;
      const filteredUsers: IUserDTO[] = selectedGroup.users.filter((_user, _ix) => {
        if (_user.id !== user.id) {
          return true;
        }

        selectedUserIndex = _ix;
        return false;
      });

      filteredUsers.splice(selectedUserIndex, 0, {
        ...cloneDeep(user),
        isGroupAdmin: event.target.checked,
      });

      updateSelectedGroupMembers(filteredUsers, false);
    },
    [selectedGroup, selectedRows, tempState],
  );

  const groupData: object[] = useMemo(
    () =>
      selectedGroup?.users?.map((user: UserDTO) => ({
        id: user.id,
        userName: user.name,
        groupAdmin: (
          <input
            type="checkbox"
            key={user.id}
            id={`${user.id}`}
            checked={user.isGroupAdmin}
            onChange={(e) => handleGroupAdminSelection(e, user)}
          />
        ),
      })) || [],
    [selectedGroup, tempState, selectedRows],
  );

  const handleRemoveMemberFromGroup = useCallback(() => {
    const users: UserDTO[] = selectedGroup.users.filter(
      (group) => !selectedUserIds.includes(group.id),
    );

    if (groupsNotAllowedToBeRemoved.includes(selectedGroup?.name)) {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        `not allowed to remove members from ${groupsNotAllowedToBeRemoved.toString()} group(s)`,
      );
      return;
    }

    setTempState((prv) => {
      const filteredUsers = prv.users.map((_user: UserDTO, _ix) => {
        if (!selectedUserIds.includes(_user.id)) {
          return _user;
        }

        const filteredGroups = _user.groups.filter(
          (u) => !(selectedUserIds.includes(_user.id) && selectedGroup.id === u.id),
        );
        _user.groups = filteredGroups;

        return _user;
      });

      return {
        ...prv,
        users: filteredUsers,
      };
    });

    updateSelectedGroupMembers(users);
  }, [selectedUserIds, selectedGroup]);

  const handleAddMember = useCallback(() => {
    setShowModal(true);
  }, []);

  const handleSelectionChange = useCallback(
    (userIndices: number[]) => {
      setSelectedRows(userIndices);
      setSelectedMembers(
        selectedGroup.users.filter((_v, _ix) => userIndices.includes(_ix)),
      );
    },
    [setSelectedMembers, selectedGroup, tempState],
  );

  return (
    <>
      <HeaderBar
        title={'Group Member'}
        onAdd={handleAddMember}
        onDelete={handleRemoveMemberFromGroup}
        disableAdd={!selectedGroup}
        enableEditDelete={
          selectedGroup &&
          selectedUserIds.length > 0 &&
          (!groupsNotAllowedToBeRemoved.includes(selectedGroup?.name) ||
            selectedMembers.length !== 0)
        }
      />
      <div
        id="membergroup-table-container"
        className="user-setup-table table-main-container h-[85%]"
        style={{ ...styles.tableContainer }}
      >
        <RMTable
          columns={getTableColumns}
          data={groupData}
          onCheckboxSelectionChange={handleSelectionChange}
          selectedIds={selectedRows}
          mainContainerStyle={styles.tableMainContainer}
        />
      </div>
      <RMModal
        id="membergroup-modal"
        setShowModal={setShowModal}
        showModal={showModal}
        body={
          <UserList
            updateGroupMembers={updateSelectedGroupMembers}
            selectedGroup={selectedGroup}
            toggleModalShow={setShowModal}
          />
        }
        heading={'User List'}
        showButtons={false}
      />
    </>
  );
};

const styles: IStyle = {
  headerContainer: {
    marginTop: '1%',
    marginRight: '2.5%',
    marginBottom: 0,
  },
  headerTitle: {
    fontWeight: 'bold',
  },
  userListRoot: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    paddingBottom: 20,
    justifyItems: 'center',
  },
  userListButtons: {
    marginRight: '3%',
  },
  userTableContainerStyle: {
    height: 350,
  },
};

export default MemberGroup;
